import React, { useState, useRef } from "react";
import {
  Card, Row, Col, Input, Button, Descriptions, Tag, Typography,
  Spin, Empty, message, Space, Divider, Tooltip 
} from "antd";
import {
  IdcardOutlined, SearchOutlined, GiftOutlined,
  FileProtectOutlined, UserOutlined, FilePdfOutlined,DownloadOutlined 
} from "@ant-design/icons";
import jsPDF from "jspdf";
import {
  obtenerResultadosPorCedula,
  obtenerKokamPorIdentificacion,
} from "../../services/reporteRit/informeReportesServices";
import InformePrintTemplate from "./informePDF";

const { Title, Text } = Typography;

// ── Resuelve nombre de depto/municipio cruzando con el registro Kokam de la misma cédula ──
const obtenerUbicacionKokam = (kokamData, sujetoid) => {
  const registro =
    kokamData?.find((k) => k.identificacion === sujetoid) || kokamData?.[0] || null;

  return {
    departamento: registro?.deptoParticipante || null,
    municipio: registro?.municipioParticipante || null,
  };
};

// ── Formatea "Nombre (código)" si hay nombre resuelto, o solo el código si no ──
const renderUbicacion = (nombreResuelto, codigo) => {
  if (!codigo && !nombreResuelto) return "—";
  if (nombreResuelto) return `${nombreResuelto} (${codigo ?? "—"})`;
  return String(codigo);
};

const TagSiNo = ({ valor }) => {
  if (!valor) return <Tag>—</Tag>;
  const esSi = String(valor).toUpperCase() === "SI";
  return <Tag color={esSi ? "success" : "error"}>{valor}</Tag>;
};

const TagEstado = ({ estado }) => (
  <Tag color={String(estado) === "1" ? "success" : "default"}>
    {String(estado) === "1" ? "Activo" : "Inactivo"}
  </Tag>
);

const formatFecha = (valor) => {
  if (!valor) return "—";
  const fecha = new Date(valor);
  return isNaN(fecha.getTime()) ? valor : fecha.toLocaleString("es-CO");
};

// ── Miniatura de un soporte (foto beneficio / doc frontal / firma) ─────────
const SoporteThumb = ({ label, archivo, onAmpliar }) => {
  const disponible = archivo?.estado === "ok" && archivo?.base64;

  return (
    <div style={{ textAlign: "center", width: 96 }}>
      {disponible ? (
        <img
          src={archivo.base64}
          alt={label}
          onClick={() => onAmpliar(archivo.base64, label)}
          style={{
            width: 88,
            height: 88,
            objectFit: "cover",
            borderRadius: 8,
            border: "1px solid #e5e5e5",
            cursor: "pointer",
          }}
        />
      ) : (
        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: 8,
            border: "1px dashed #d9d9d9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
            color: "#bbb",
            fontSize: 20,
          }}
          title={archivo?.mensaje || "No disponible"}
        >
          ⚠
        </div>
      )}
      <div style={{ fontSize: 11, marginTop: 4, color: "#666" }}>{label}</div>
    </div>
  );
};

// ── Fila de 3 miniaturas para una encuesta de entrega (573 / 667) ──────────
const SoportesEntregaRow = ({ soportes, onAmpliar }) => {
  if (!soportes) return null;

  return (
    <>
      <Divider style={{ margin: "16px 0 12px" }}>Soportes</Divider>
      <Space size={16} wrap style={{ justifyContent: "center", width: "100%" }}>
        <SoporteThumb label="Foto beneficio" archivo={soportes.fotoBeneficio} onAmpliar={onAmpliar} />
        <SoporteThumb label="Doc. frontal" archivo={soportes.docFrontal} onAmpliar={onAmpliar} />
        <SoporteThumb label="Firma" archivo={soportes.firmaPersonal} onAmpliar={onAmpliar} />
      </Space>
    </>
  );
};

// ── Modal simple para ver la imagen ampliada ────────────────────────────────
const ModalImagen = ({ imagen, onClose }) => {
  if (!imagen) return null;

  const descargarImagen = () => {
    const enlace = document.createElement("a");
    enlace.href = imagen.src;
    enlace.download = `${imagen.label}.jpg`;
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 10,
          padding: 20,
          maxWidth: "90vw",
          maxHeight: "90vh",
          boxShadow: "0 8px 24px rgba(0,0,0,.25)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Text strong style={{ fontSize: 18 }}>
            {imagen.label}
          </Text>

          <Space size={8}>
            <Tooltip title="Descargar imagen">
              <Button
                type="primary"
                shape="circle"
                icon={<DownloadOutlined />}
                onClick={descargarImagen}
              />
            </Tooltip>

            <Tooltip title="Cerrar">
              <Button
                shape="circle"
                onClick={onClose}
              >
                ✕
              </Button>
            </Tooltip>
          </Space>
        </div>

        <img
          src={imagen.src}
          alt={imagen.label}
          style={{
            display: "block",
            maxWidth: "100%",
            maxHeight: "75vh",
            borderRadius: 8,
            margin: "0 auto",
          }}
        />
      </div>
    </div>
  );
};
const InformeConsolidadoRit = () => {
  const [cedula, setCedula]             = useState("");
  const [loading, setLoading]           = useState(false);
  const [informe, setInforme]           = useState(null);
  const [kokamData, setKokamData]       = useState([]);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const [imagenAmpliada, setImagenAmpliada] = useState(null); // { src, label }
  const ubicacionKokam = obtenerUbicacionKokam(kokamData, informe?.sujetoid);
  const printRef = useRef(null);

  const abrirImagen = (src, label) => setImagenAmpliada({ src, label });
  const cerrarImagen = () => setImagenAmpliada(null);

  // ── Consulta ──────────────────────────────────────────────────────────────
  const consultar = async () => {
    const valor = cedula.trim();
    if (!valor) {
      message.warning("Ingresa un número de documento");
      return;
    }
    setLoading(true);
    setInforme(null);
    setKokamData([]);

    try {
      const [resRit, resKokam] = await Promise.allSettled([
        obtenerResultadosPorCedula(valor),
        obtenerKokamPorIdentificacion(valor),
      ]);

      if (resRit.status === "fulfilled" && resRit.value?.ok) {
        setInforme(resRit.value.data);
      } else {
        message.warning("No se encontró información en el reporte RIT");
      }

      if (resKokam.status === "fulfilled" && resKokam.value?.ok) {
        setKokamData(resKokam.value.data);
      }
    } catch (error) {
      console.error(error);
      message.error("Error al consultar la información");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") consultar();
  };

  // ── Generar PDF profesional (2 cards por hoja, sin cortes) ──────────────
  const generarPdf = async () => {
    if (!informe || !printRef.current) return;
    setGenerandoPdf(true);
    try {
      const { headerCanvas, infoCanvas, secciones, footerCanvas } =
        await printRef.current.capturar();

      const pdf        = new jsPDF("p", "mm", "a4");
      const pageWidth   = pdf.internal.pageSize.getWidth();
      const pageHeight  = pdf.internal.pageSize.getHeight();
      const margin      = 10;
      const usableWidth = pageWidth - margin * 2;

      const alturaEnMm = (canvas) => (canvas.height * usableWidth) / canvas.width;

      const agregarImagen = (canvas, y) => {
        const h = alturaEnMm(canvas);
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", margin, y, usableWidth, h);
        return h;
      };

      let cursorY = margin;

      if (headerCanvas) cursorY += agregarImagen(headerCanvas, cursorY) + 4;
      if (infoCanvas)    cursorY += agregarImagen(infoCanvas, cursorY) + 6;

      secciones.forEach((canvas, idx) => {
        if (!canvas) return;

        const esInicioDePar = idx % 2 === 0;
        if (idx > 0 && esInicioDePar) {
          pdf.addPage();
          cursorY = margin;
        }

        const h = alturaEnMm(canvas);

        if (cursorY + h > pageHeight - margin && cursorY !== margin) {
          pdf.addPage();
          cursorY = margin;
        }

        cursorY += agregarImagen(canvas, cursorY) + 6;
      });

      if (footerCanvas) {
        const hFooter = alturaEnMm(footerCanvas);
        if (cursorY + hFooter > pageHeight - margin) {
          pdf.addPage();
          cursorY = margin;
        }
        agregarImagen(footerCanvas, cursorY);
      }

      pdf.save(`Informe_RIT_${informe.sujetoid}_${new Date().toISOString().slice(0, 10)}.pdf`);
      message.success("PDF generado correctamente");
    } catch (error) {
      console.error(error);
      message.error("Error al generar el PDF");
    } finally {
      setGenerandoPdf(false);
    }
  };

  // ── Card 1: Caracterización (509) ────────────────────────────────────────
  const renderCard509 = () => {
    const items = informe?.encuesta_509 || [];
    return (
      <Card
        title={<Space><IdcardOutlined /> Reporte RIT 509</Space>}
        headStyle={{ background: "#e6f4ff", fontWeight: 600 }}
        bordered
        style={{ height: "100%" }}
      >
        {items.length === 0 ? (
          <Empty description="Sin información de caracterización" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          items.map((item, idx) => (
            <div key={item.id ?? idx} style={{ marginBottom: idx < items.length - 1 ? 16 : 0 }}>
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Documento">{item.documento || "—"}</Descriptions.Item>
                <Descriptions.Item label="Nombre titular">{item.nombreTitular || "—"}</Descriptions.Item>
                <Descriptions.Item label="Fecha nacimiento">{item.fechaNacimiento || "—"}</Descriptions.Item>
                <Descriptions.Item label="Celular">{item.celular || "—"}</Descriptions.Item>
                <Descriptions.Item label="Correo">{item.correo || "—"}</Descriptions.Item>
                <Descriptions.Item label="Dirección">{item.direccion || "—"}</Descriptions.Item>
                <Descriptions.Item label="Barrio">{item.barrio || "—"}</Descriptions.Item>
                <Descriptions.Item label="Dpto. atención">
                  {renderUbicacion(ubicacionKokam.departamento, item.codDptoAtencion)}
                </Descriptions.Item>
                <Descriptions.Item label="Municipio atención">
                  {renderUbicacion(ubicacionKokam.municipio, item.codMunicipioAtencion)}
                </Descriptions.Item>
                <Descriptions.Item label="Proyecto">{item.nombreProyecto || "—"}</Descriptions.Item>
                <Descriptions.Item label="Desea participar"><TagSiNo valor={item.deseaParticipar} /></Descriptions.Item>
                <Descriptions.Item label="Fecha diligenciamiento">{formatFecha(item.fechaDiligenciamiento)}</Descriptions.Item>
                <Descriptions.Item label="Estado"><TagEstado estado={item.estado} /></Descriptions.Item>
                <Descriptions.Item label="Fecha enviado">{item.fechaEnviado || "—"}</Descriptions.Item>
              </Descriptions>
              {idx < items.length - 1 && <Divider style={{ margin: "16px 0" }} />}
            </div>
          ))
        )}
      </Card>
    );
  };

  // ── Card genérico para entregas (573 / 667), ahora con soportes y ubicación resuelta ──
  const renderCardEntrega = (titulo, items, color, soportes) => (
    <Card
      title={<Space><GiftOutlined /> {titulo}</Space>}
      headStyle={{ background: color, fontWeight: 600 }}
      bordered
      style={{ height: "100%" }}
    >
      {items.length === 0 ? (
        <Empty description="Sin información de entrega" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        items.map((item, idx) => (
          <div key={item.id ?? idx} style={{ marginBottom: idx < items.length - 1 ? 16 : 0 }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="N° documento titular">{item.numeroDocumentoTitular || "—"}</Descriptions.Item>
              <Descriptions.Item label="N° entrega">{item.nroEntrega || "—"}</Descriptions.Item>
              <Descriptions.Item label="Nombre completo">{item.nombreCompleto || item.nombreTitular || "—"}</Descriptions.Item>
              <Descriptions.Item label="Quién reclama">{item.quienReclamaBeneficio || "—"}</Descriptions.Item>
              <Descriptions.Item label="Departamento">
                {renderUbicacion(ubicacionKokam.departamento, item.departamento)}
              </Descriptions.Item>
              <Descriptions.Item label="Municipio">
                {renderUbicacion(ubicacionKokam.municipio, item.municipio)}
              </Descriptions.Item>
              <Descriptions.Item label="Punto de entrega">{item.puntoEntrega || "—"}</Descriptions.Item>
              <Descriptions.Item label="Proyecto">{item.nombreProyecto || "—"}</Descriptions.Item>
              <Descriptions.Item label="Fecha diligenciamiento">{formatFecha(item.fechaDiligenciamiento)}</Descriptions.Item>
              <Descriptions.Item label="Estado"><TagEstado estado={item.estado} /></Descriptions.Item>
              <Descriptions.Item label="Fecha enviado">{item.fechaEnviado || "—"}</Descriptions.Item>
            </Descriptions>
            {idx < items.length - 1 && <Divider style={{ margin: "16px 0" }} />}
          </div>
        ))
      )}

      <SoportesEntregaRow soportes={soportes} onAmpliar={abrirImagen} />
    </Card>
  );

  // ── Card 4: Reporte Kokam (datos reales) ─────────────────────────────────
  const renderCardKokam = () => (
    <Card
      title={<Space><FileProtectOutlined /> Reporte Kokam</Space>}
      headStyle={{ background: "#fff7e6", fontWeight: 600 }}
      bordered
      style={{ height: "100%" }}
    >
      {kokamData.length === 0 ? (
        <Empty description="Sin información en el reporte Kokam" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        kokamData.map((item, idx) => (
          <div key={`${item.identificacion}-${idx}`} style={{ marginBottom: idx < kokamData.length - 1 ? 16 : 0 }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Identificación">{item.identificacion || "—"}</Descriptions.Item>
              <Descriptions.Item label="Nombre completo">{item.nombreCompleto || "—"}</Descriptions.Item>
              <Descriptions.Item label="Municipio">{item.municipioParticipante || "—"}</Descriptions.Item>
              <Descriptions.Item label="Departamento">{item.deptoParticipante || "—"}</Descriptions.Item>
              <Descriptions.Item label="Promotor">
                {[item.nombresPromotor, item.apellidosPromotor].filter(Boolean).join(" ") || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Cédula promotor">{item.cedulaPromotor || "—"}</Descriptions.Item>
              <Descriptions.Item label="Teléfono">{item.telefono || "—"}</Descriptions.Item>
              <Descriptions.Item label="Dirección">{item.direccion || "—"}</Descriptions.Item>
              <Descriptions.Item label="Aprobación pago">
                <TagSiNo valor={item.aprobacionPago} />
              </Descriptions.Item>
            </Descriptions>
            {idx < kokamData.length - 1 && <Divider style={{ margin: "16px 0" }} />}
          </div>
        ))
      )}
    </Card>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Card style={{ margin: 20 }}>
      <Title level={3}>Informe Consolidado RIT</Title>

      <Space style={{ marginBottom: 20 }} wrap>
        <Input
          placeholder="Número de documento del titular"
          prefix={<UserOutlined style={{ color: "#bbb" }} />}
          value={cedula}
          onChange={(e) => setCedula(e.target.value.replace(/\D/g, ""))}
          onKeyDown={handleKeyDown}
          style={{ width: 260 }}
          allowClear
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={consultar} loading={loading}>
          Consultar
        </Button>
        {informe && (
          <Button
            icon={<FilePdfOutlined />}
            onClick={generarPdf}
            loading={generandoPdf}
            style={{ borderColor: "#c41d1d", color: generandoPdf ? undefined : "#c41d1d" }}
          >
            {generandoPdf ? "Generando PDF..." : "Descargar PDF"}
          </Button>
        )}
      </Space>

      <Spin spinning={loading} tip="Consultando información..." size="large">
        {informe ? (
          <>
            <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
              Sujeto consultado: <Text strong>{informe.sujetoid}</Text>
            </Text>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} md={12}>{renderCard509()}</Col>
              <Col xs={24} md={12}>
                {renderCardEntrega(
                  "Entregas 1 - 573",
                  informe.encuesta_573 || [],
                  "#f6ffed",
                  informe.soportes_573
                )}
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                {renderCardEntrega(
                  "Entregas 2 - 667",
                  informe.encuesta_667 || [],
                  "#fff0f6",
                  informe.soportes_667
                )}
              </Col>
              <Col xs={24} md={12}>{renderCardKokam()}</Col>
            </Row>
          </>
        ) : (
          !loading && (
            <Empty
              description="Ingresa un número de documento para generar el informe"
              style={{ padding: "60px 0" }}
            />
          )
        )}
      </Spin>

      {/* Plantilla oculta usada solo para generar el PDF */}
      <InformePrintTemplate ref={printRef} informe={informe} kokamData={kokamData} />

      {/* Modal para ver soportes ampliados */}
      <ModalImagen imagen={imagenAmpliada} onClose={cerrarImagen} />
    </Card>
  );
};

export default InformeConsolidadoRit;
